<script setup>
/* Passwort setzen nach Einladung oder nach "Passwort vergessen".
 *
 * Wird in App.vue gezeigt, solange die Sitzung aus einem Einladungs- oder
 * Zurücksetzen-Link stammt (hasInviteLink in useAuth.js). Beide Fälle
 * brauchen dieselbe Maske — schon die alte Fassung der App hatte dafür nur
 * eine gemeinsame Ansicht, siehe src/_legacy/legacy-body.html:83.
 */
import { ref } from 'vue';
import { useAuth, validateNewPassword } from '../composables/useAuth.js';
import PasswordField from './PasswordField.vue';

const emit = defineEmits(['done']);
const { updatePassword } = useAuth();

const password = ref('');
const confirmPassword = ref('');
const busy = ref(false);
const error = ref(null);

async function submit() {
  error.value = validateNewPassword(password.value, confirmPassword.value);
  if (error.value) return;

  busy.value = true;
  try {
    await updatePassword(password.value);
    /* Der Einladungs-/Zurücksetzen-Link darf nach Gebrauch nicht in der
       Adresszeile stehen bleiben. */
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    emit('done');
  } catch (e) {
    error.value = `errors.${e.message}`;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="signin">
    <p class="intro">{{ $t('auth.setPassword.intro') }}</p>
    <form @submit.prevent="submit">
      <label class="field">
        <span>{{ $t('auth.setPassword.newPassword') }}</span>
        <PasswordField v-model="password" autocomplete="new-password" required />
      </label>
      <label class="field">
        <span>{{ $t('auth.setPassword.confirmPassword') }}</span>
        <PasswordField v-model="confirmPassword" autocomplete="new-password" required />
      </label>

      <p v-if="error" class="msg error">{{ $t(error) }}</p>

      <button type="submit" class="primary" :disabled="busy">
        {{ busy ? $t('common.loading') : $t('auth.setPassword.submit') }}
      </button>
    </form>
  </div>
</template>

<style scoped>
/* Deckt sich bewusst mit SignInPanel.vue -- dieselbe Art Bildschirm, an
   derselben Stelle im Layout. */
.signin {
  max-width: 340px;
  margin: 12px auto 0;
  padding: 26px 24px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
}
.intro {
  font-size: 0.82rem;
  color: var(--muted);
  line-height: 1.5;
  margin-bottom: 16px;
}
.field {
  display: block;
  margin-bottom: 14px;
}
.field span {
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dim);
  margin-bottom: 6px;
}
.field :deep(input) {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 9px;
  color: var(--text);
  font-family: var(--sans);
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.18s;
}
.field :deep(input:focus) {
  border-color: var(--green-dim);
}
.primary {
  width: 100%;
  padding: 11px;
  margin-top: 4px;
  background: var(--green);
  border: 0;
  border-radius: 9px;
  color: rgb(var(--v-theme-on-primary));
  font-family: var(--sans);
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.18s;
}
.primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.msg {
  font-size: 0.78rem;
  line-height: 1.5;
  margin-bottom: 12px;
}
.msg.error {
  color: var(--red);
}
</style>
